import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createClaimLink } from '@services/partnerService';
import { usePartnerAuth } from '../PartnerAuthContext';
import { Badge, Button, C, Card, ErrorNote, Muted, SectionTitle, Title } from '../ui';
import { fmtDate, useCommerces } from '../useCommerces';

const Row = ({ label, value }: { label: string; value: string }) => (
  <div style={{ display: 'flex', padding: '8px 0', borderTop: `1px solid ${C.cardBorder}`, fontSize: 14 }}>
    <span style={{ width: 160, color: C.muted, flexShrink: 0 }}>{label}</span>
    <span style={{ color: C.primary }}>{value || '—'}</span>
  </div>
);

const CommerceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { token } = usePartnerAuth();
  const navigate = useNavigate();
  const { commerces, loading } = useCommerces();
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [genLoading, setGenLoading] = useState(false);

  const c = commerces.find((x) => x.id_comercio === id);
  const claimed = c?.estado === 'reclamado';

  const onClaimLink = async () => {
    if (!token || !id) return;
    setError(null);
    setGenLoading(true);
    try {
      const res = await createClaimLink(token, id);
      setLink(`${window.location.origin}/pre-registro/${res.token}`);
    } catch {
      setError('No se pudo generar el link de claim.');
    } finally {
      setGenLoading(false);
    }
  };

  if (loading) return <Muted>Cargando...</Muted>;
  if (!c) {
    return (
      <>
        <Title>Comercio no encontrado</Title>
        <Button variant="secondary" onClick={() => navigate('/partner/comercios')}>
          Volver a comercios
        </Button>
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <Title>{c.negocio_nombre || 'Comercio sin nombre'}</Title>
        <Badge tone={claimed ? 'claimed' : 'draft'}>{claimed ? 'Reclamado' : 'Borrador'}</Badge>
      </div>

      <Card>
        <SectionTitle>Informacion</SectionTitle>
        <Row label="Negocio" value={c.negocio_nombre || ''} />
        <Row label="Dueno" value={c.owner_nombre || ''} />
        <Row label="WhatsApp" value={c.owner_telefono || ''} />
        <Row label="Email" value={c.owner_correo || ''} />
        <Row label="Plan" value={c.tipo_plan || 'FREEMIUM'} />
        <Row label="Estado" value={claimed ? `Reclamado el ${fmtDate(c.claimed_at)}` : 'Borrador (sin reclamar)'} />
        <Row label="Creado" value={fmtDate(c.created_at)} />
      </Card>

      {!claimed && (
        <Card>
          <SectionTitle>Acciones</SectionTitle>
          <Muted style={{ marginTop: 0 }}>
            Carga el menu del comercio o genera el link para que el dueno reclame su cuenta (valido 72h).
          </Muted>
          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
            <Button onClick={() => navigate(`/partner/draft/${c.id_comercio}/menu`)}>Cargar menu</Button>
            <Button variant="secondary" onClick={onClaimLink} disabled={genLoading}>
              {genLoading ? 'Generando...' : 'Generar link de claim'}
            </Button>
          </div>
          {error && <ErrorNote>{error}</ErrorNote>}
          {link && (
            <div style={{ marginTop: 14 }}>
              <Muted style={{ marginTop: 0, marginBottom: 6 }}>Comparte este link con el dueno:</Muted>
              <input
                readOnly
                value={link}
                onFocus={(e) => e.currentTarget.select()}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: 13,
                  border: `1px solid ${C.inputBorder}`,
                  borderRadius: 6,
                  boxSizing: 'border-box',
                  fontFamily: 'Poppins, sans-serif',
                  color: C.secondaryDark,
                }}
              />
            </div>
          )}
        </Card>
      )}
    </>
  );
};

export default CommerceDetail;
